import { isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import {
  Animated,
  Dimensions,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { Colors, HEADER_HEIGHT, TOAST_DELAY_DURATION, grid } from '@shared/styles';
import { useIsDesktop, usePrevious } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { ToastAction, ToastOptions, ToastState } from './MBToastTypes';
import { useToastFling } from './gestures';

interface Props {
  options: ToastOptions;
  state: ToastState;
  onDismiss(): void;
  onExit(): void;
  showClose?: boolean;
}

export function MBToast({ options, state, onDismiss, onExit }: Props) {
  const { layout, style, message, duration } = options;

  const isDesktop = useIsDesktop();
  const isConfirmation = style === 'confirmation';
  const isWarning = style === 'warning';
  const isModal = style === 'modal';
  const isDefault = style === 'default';
  const isLongLayout = layout === 'long';

  const textColor = isWarning ? Colors.v2.purple : Colors.v2.black;
  const icon = getToastIcon(options);
  const opacity = useRef<Animated.AnimatedValue>(new Animated.Value(1)).current;
  const scale = useRef<Animated.AnimatedValue>(new Animated.Value(1)).current;
  const translateX = useRef(new Animated.Value(Dimensions.get('screen').width)).current;
  const autoDismissTimerRef = useRef<number | undefined>(undefined);

  const animIn = useRef(
    Animated.timing(translateX, {
      toValue: 0,
      useNativeDriver: true,
      duration: 300,
      delay: options.delay ?? TOAST_DELAY_DURATION,
    }),
  ).current;

  const animOut = useRef(
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        useNativeDriver: true,
        duration: 200,
      }),
      Animated.timing(scale, {
        toValue: 0.9,
        useNativeDriver: true,
        duration: 200,
      }),
    ]),
  ).current;

  useEffect(() => {
    animIn.start();
    if (duration && !isModal) {
      autoDismissTimerRef.current = setTimeout(() => {
        animIn.stop();
        onDismiss();
      }, duration) as any;
    }
    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
      }
    };
  }, [duration, isModal, animIn, onDismiss]);

  const prevState = usePrevious(state);
  useEffect(() => {
    if (prevState !== 'anim-out' && state === 'anim-out') {
      clearTimeout(autoDismissTimerRef.current);
      animIn.stop();
      animOut.start(onExit);
    }
  }, [prevState, state, animIn, animOut, onExit]);

  const { panHandlers } = useToastFling(onExit, translateX);

  const containerStyle: StyleProp<ViewStyle> = [
    styles.container,
    !isDesktop && styles.containerMobile,
    isModal && styles.modalPosition,
    isConfirmation && styles.confirmationPosition,
    isWarning && styles.warningPosition,
    isDefault && styles.defaultPosition,
    isDefault && !isDesktop && styles.defaultPositionMobile,
    isDesktop && !isModal && styles.nonModalPosition,
  ];

  const toastStyle: StyleProp<ViewStyle> = [
    styles.toast,
    isLongLayout && styles.longToast,
    isConfirmation && styles.confirmationToast,
    isWarning && styles.warningToast,
  ];

  return (
    <>
      {isModal && <MBView style={styles.modalBackdrop} />}
      <Animated.View
        style={[containerStyle, { opacity, transform: [{ scale }, { translateX }] }]}
        {...(!isModal && panHandlers)}
      >
        <MBView style={[styles.containerPadding, !isDesktop && styles.containerPaddingMobile]}>
          <TouchableOpacity
            onPress={onDismiss}
            activeOpacity={1}
            disabled={isDefault || isModal}
            testID="toast-message"
          >
            <MBView style={toastStyle}>
              <MBView
                row
                centerVertically
                style={[styles.toastContentRow, isLongLayout && styles.longToastContentRow]}
                paddingHorizontal={2}
              >
                {icon && (
                  <View paddingRight={1}>
                    <MBIconV2 name={icon} color={Colors.v2.purple} size={grid(5)} />
                  </View>
                )}
                <MBView flex style={[styles.toastMessage, isLongLayout && styles.longToastMessage]}>
                  <MBTextBody1 color={textColor}>{message}</MBTextBody1>
                </MBView>
                {!isLongLayout && <ToastActionButtons options={options} onDismiss={onDismiss} />}
              </MBView>
              {isLongLayout && <ToastActionButtons options={options} onDismiss={onDismiss} />}
            </MBView>
          </TouchableOpacity>
        </MBView>
      </Animated.View>
    </>
  );
}

function ToastActionButtons({ options, onDismiss }: Pick<Props, 'options' | 'onDismiss'>) {
  const { actions, layout, style, showClose = true } = options;
  const isDesktop = useIsDesktop();
  const isDefault = style === 'default';
  const isLongLayout = layout === 'long';
  const hasActions = actions && actions.length > 0;
  const shouldRender = hasActions || showClose;
  if (!shouldRender) {
    return null;
  }

  return (
    <MBView
      row
      centerVertically
      style={[styles.toastButtonRow, isLongLayout && styles.longToastButtonRow]}
    >
      {actions?.map((action, index) => (
        <ToastActionButton
          key={index}
          action={action}
          onDismiss={onDismiss}
          isLongLayout={isLongLayout}
        />
      ))}
      {isDesktop && isDefault && isLongLayout && showClose && (
        <ToastActionButton
          action={{
            label: i18n('common.close')?.toUpperCase(),
            onPress: onDismiss,
          }}
          onDismiss={onDismiss}
          isLongLayout={isLongLayout}
          testID="toast-close-button"
        />
      )}
      {isDesktop && isDefault && !isLongLayout && (
        <TouchableOpacity
          style={styles.action}
          onPress={onDismiss}
          key="close"
          testID="toast-close-button"
        >
          <MBIconV2 name="exit-small" color={Colors.v2.purple} size={grid(3)} />
        </TouchableOpacity>
      )}
    </MBView>
  );
}

function ToastActionButton({
  action,
  onDismiss,
  isLongLayout,
  testID,
}: Pick<Props, 'onDismiss'> & { action: ToastAction; isLongLayout: boolean; testID?: string }) {
  const onPress = () => {
    action.onPress?.();
    onDismiss();
  };

  return (
    <TouchableOpacity
      style={[styles.action, isLongLayout && styles.longToastAction]}
      onPress={onPress}
      disabled={!action.onPress}
      testID={testID}
    >
      {action.label ? (
        <MBTextBody1Bold>{action.label?.toUpperCase() ?? ''}</MBTextBody1Bold>
      ) : (
        action.element
      )}
    </TouchableOpacity>
  );
}

function getToastIcon(options: ToastOptions): MBIconV2Name | null {
  const { icon, style } = options;
  if (!isNil(icon)) {
    return icon;
  }

  switch (style) {
    case 'confirmation':
      return 'check-filled';
    case 'warning':
      return 'remove-x';
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    zIndex: 1000000,
  },
  containerMobile: {
    width: '100%',
  },
  containerPadding: {
    paddingHorizontal: grid(4),
  },
  containerPaddingMobile: {
    paddingHorizontal: grid(1),
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  toast: {
    maxWidth: grid(54),
    width: '100%',
    borderRadius: grid(1),
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: grid(0.5),
    shadowOpacity: 0.4,
    elevation: grid(0.5),
    backgroundColor: Colors.v2.green10,
  },
  longToast: {
    paddingTop: grid(1),
  },
  modalPosition: {
    top: '45%',
  },
  nonModalPosition: {
    width: 'auto',
    right: 0,
  },
  confirmationPosition: {
    top: grid(2) + HEADER_HEIGHT,
  },
  confirmationToast: {
    backgroundColor: Colors.v2.green,
  },
  warningPosition: {
    top: grid(2) + HEADER_HEIGHT,
  },
  warningToast: {
    backgroundColor: Colors.v2.actionTropicalRed,
  },
  defaultPosition: {
    bottom: grid(12),
  },
  defaultPositionMobile: {
    bottom: grid(12),
  },
  toastContentRow: {
    minHeight: grid(7),
  },
  longToastContentRow: {
    minHeight: grid(0),
    paddingRight: grid(3),
  },
  toastMessage: {
    paddingTop: grid(1.5),
    paddingBottom: grid(1.5),
    paddingRight: grid(1),
  },
  longToastMessage: {
    paddingTop: grid(1.25),
    paddingBottom: grid(1.25),
  },
  toastButtonRow: {
    paddingLeft: grid(3),
  },
  longToastButtonRow: {
    justifyContent: 'flex-end',
    paddingBottom: grid(0.5),
    paddingHorizontal: grid(1.5),
  },
  action: {
    height: grid(5),
    paddingHorizontal: grid(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: grid(2),
  },
  longToastAction: {
    paddingHorizontal: grid(1.5),
  },
});
